import { applyTransaction } from "@datorama/akita";
import ApiClientInterface from "@lib/ApiClient/ApiClientInterface";
import { Observable } from "rxjs";
import { catchError, finalize, map, tap } from "rxjs/operators";
import { create } from "superstruct";
import { singleton } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import TikTokPostsResponseEntity from "../entities/TikTokPostsRresponseEntity";
import TikTokPostsResponseSchema from "../schemas/TikTokPostsResponseSchema";
import { TikTokPostsStore } from "../stores/TikTokPostsStore";

interface GetPostsRequestParamsDTO {
    galleryId: number;
    perPage: number;
    offset?: number;
    sortBy?: "newest_first" | "tagged_first";
}

@singleton()
export class TikTokPostsService {
    constructor(
        private tikTokPostsStore: TikTokPostsStore,
        private readonly apiClient: ApiClientInterface,
        private readonly logger: LoggerInterface
    ) {}

    getPosts(
        reqParams: GetPostsRequestParamsDTO
    ): Observable<TikTokPostsResponseEntity> {
        this.logger.debug("TikTokPostsService.getPosts is started", reqParams);
        this.tikTokPostsStore.setLoading(true);
        return this.apiClient
            .get({
                url: "/tiktok/getPostList",
                responseType: "json",
                queryParams: {
                    galleryId: reqParams.galleryId,
                    perPage: reqParams.perPage,
                    offset: reqParams.offset || 0,
                    sortBy: reqParams.sortBy || "newest_first",
                },
            })
            .pipe(
                finalize(() => {
                    this.tikTokPostsStore.setLoading(false);
                }),
                map((v) => create(v.body, TikTokPostsResponseSchema)),
                catchError((error: unknown) => {
                    this.logger.debug(
                        "TikTokPostsService.getPosts is errored",
                        reqParams
                    );
                    this.tikTokPostsStore.setError(true);
                    throw error;
                }),
                tap(() => {
                    this.logger.debug(
                        "TikTokPostsService.getPosts is ready",
                        reqParams
                    );
                })
            );
    }

    getPostsByGalleryId(
        reqParams: GetPostsRequestParamsDTO
    ): Observable<TikTokPostsResponseEntity> {
        return this.getPosts(reqParams).pipe(
            tap((v) => {
                applyTransaction(() => {
                    this.tikTokPostsStore.set(v.items);
                    this.tikTokPostsStore.update(() => ({
                        paginationMeta: {
                            perPage: v.perPage,
                            totalCount: v.totalCount,
                            currentOffset: v.currentOffset,
                        },
                    }));
                });
            })
        );
    }

    loadMorePostsByGalleryId(
        reqParams: GetPostsRequestParamsDTO
    ): Observable<TikTokPostsResponseEntity> {
        return this.getPosts(reqParams).pipe(
            tap((v) => {
                applyTransaction(() => {
                    this.tikTokPostsStore.add(v.items);
                    this.tikTokPostsStore.update({
                        paginationMeta: {
                            perPage: v.perPage,
                            totalCount: v.totalCount,
                            currentOffset: v.currentOffset,
                        },
                    });
                });
            })
        );
    }
}
